import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import type { FilterExpression, UnifiedFilters } from 'types/filters';
import { isPropertyLeaf } from '@center-platform/spheres/filters';
import SphereAvatar from '../sphere-avatar/sphere-avatar';
import { useSchemaMetadata } from '../../../hooks/useSchemaMetadata';
import {
  classifyApplicability,
  type ClassifiedApplicability,
} from '../../../utils/marketplace/classifyApplicability';
import './ApplicabilityRendererMolecule.scss';

// Property names whose VALUE is a schema system_name. The fallback path
// renders these values with a friendly schema chip; everything else stays
// as plain text. Mirrors the SCHEMA_PROPERTIES set in classifyApplicability.
const SCHEMA_PROPERTIES = new Set(['type', 'label', 'schemaSystemName']);

export interface ApplicabilityRendererMoleculeProps {
  filters: UnifiedFilters | null;
  className?: string;
  header?: React.ReactNode;
}

function formatSchemaLabel(systemName: string): string {
  return systemName
    .replace(/^center_/, '')
    .replaceAll('_', ' ')
    .replaceAll(/\b\w/g, (c) => c.toUpperCase());
}

function formatFamilyLabel(family: string): string {
  return formatSchemaLabel(family);
}

function formatOperator(operator: string): string {
  return operator.replaceAll('_', ' ');
}

function collectFallbackExpressions(filters: UnifiedFilters): FilterExpression[] {
  const all: FilterExpression[] = [];
  for (const expr of filters.propertyFilters ?? []) {
    all.push(expr);
  }
  for (const group of filters.filterGroups ?? []) {
    // A lane leaf (perspective/relationship/arc) has no fallback-row
    // rendering here yet — skip it rather than misrendering it as a
    // property expression.
    for (const leaf of group.expressions ?? []) {
      if (isPropertyLeaf(leaf)) all.push(leaf);
    }
  }
  return all;
}

const ApplicabilityRendererMolecule: React.FC<
  ApplicabilityRendererMoleculeProps
> = ({ filters, className = '', header }) => {
  const { t } = useTranslation();
  const classified: ClassifiedApplicability = classifyApplicability(filters);

  // Collect every schema system_name we may need to render so we can resolve
  // them all via a single hook call (the hook caches per name).
  const schemaSystemNames = useMemo(() => {
    const names: string[] = [...classified.sphereTypes];
    if (filters) {
      for (const expr of collectFallbackExpressions(filters)) {
        if (SCHEMA_PROPERTIES.has(expr.property)) {
          if (Array.isArray(expr.values)) {
            for (const v of expr.values) if (typeof v === 'string') names.push(v);
          } else if (typeof expr.value === 'string') {
            names.push(expr.value);
          } else if (Array.isArray(expr.value)) {
            for (const v of expr.value) if (typeof v === 'string') names.push(v);
          }
        }
      }
    }
    return names;
  }, [classified.sphereTypes, filters]);

  const { resolved: schemaMetadata } = useSchemaMetadata(schemaSystemNames);

  const rootClasses = ['marketplace-applicability-renderer', className]
    .filter(Boolean)
    .join(' ');

  const renderSchemaChip = (systemName: string, excluded: boolean) => {
    const meta = schemaMetadata.get(systemName);
    const label = meta?.displayName ?? formatSchemaLabel(systemName);
    const chipClasses = [
      'marketplace-applicability-renderer__chip',
      excluded ? 'marketplace-applicability-renderer__chip--excluded' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <span key={systemName} className={chipClasses}>
        <SphereAvatar
          glyph={meta?.glyph ?? undefined}
          glyphColor={meta?.glyphColor}
          size="xsmall"
          shape="circle"
          altText={label}
        />
        <span className="marketplace-applicability-renderer__chip-label">{label}</span>
      </span>
    );
  };

  const renderTypeChips = (types: string[], excluded: boolean) => (
    <div className="marketplace-applicability-renderer__chips">
      {types.map((systemName) => renderSchemaChip(systemName, excluded))}
    </div>
  );

  // Translate platform property names to user-facing labels. `type` and
  // `label` carry sphere-schema system names — render them as "Sphere Type".
  const formatPropertyLabel = (property: string): string => {
    if (SCHEMA_PROPERTIES.has(property)) {
      return t('marketplace.approachDetail.overview.applicability.sphereType');
    }
    return property;
  };

  // Render a single fallback filter expression, replacing schema-typed values
  // with friendly chips when possible.
  const renderFallbackExpression = (
    expr: FilterExpression,
    idx: number,
  ): React.ReactNode => {
    const propertyLabel = formatPropertyLabel(expr.property);
    const operatorLabel = formatOperator(expr.operator);
    const isSchemaProp = SCHEMA_PROPERTIES.has(expr.property);

    let valuesArr: string[] = [];
    if (Array.isArray(expr.values)) {
      valuesArr = expr.values.map(String);
    } else if (Array.isArray(expr.value)) {
      valuesArr = expr.value.map(String);
    } else if (expr.value !== undefined && expr.value !== null) {
      valuesArr = [String(expr.value)];
    }

    return (
      <li
        key={`${expr.property}-${idx}`}
        className="marketplace-applicability-renderer__filter-row"
      >
        <span className="marketplace-applicability-renderer__filter-prop">
          {propertyLabel}
        </span>
        <span className="marketplace-applicability-renderer__filter-op">
          {operatorLabel}
        </span>
        {isSchemaProp ? (
          <span className="marketplace-applicability-renderer__chips">
            {valuesArr.map((v) => renderSchemaChip(v, false))}
          </span>
        ) : (
          <span className="marketplace-applicability-renderer__filter-value">
            {valuesArr.join(', ')}
          </span>
        )}
      </li>
    );
  };

  const renderBody = () => {
    if (classified.mode === 'all') {
      return (
        <div className="marketplace-applicability-renderer__row marketplace-applicability-renderer__row--all">
          <i className="pi pi-check-circle" aria-hidden="true" />
          <div>
            <div className="marketplace-applicability-renderer__heading">
              {t('marketplace.approachDetail.overview.applicability.anySphere')}
            </div>
            <div className="marketplace-applicability-renderer__intro">
              {t(
                'marketplace.approachDetail.overview.applicability.anySphereDescription',
              )}
            </div>
          </div>
        </div>
      );
    }

    if (classified.mode === 'family') {
      const family = classified.families?.[0] ?? '';
      const familyLabel = formatFamilyLabel(family);
      return (
        <div className="marketplace-applicability-renderer__row">
          <i className="pi pi-sitemap" aria-hidden="true" />
          <div>
            <div className="marketplace-applicability-renderer__heading">
              {t('marketplace.approachDetail.overview.applicability.family', {
                family: familyLabel,
              })}
            </div>
            <div className="marketplace-applicability-renderer__intro">
              {t(
                'marketplace.approachDetail.overview.applicability.familyIntro',
                { family: familyLabel },
              )}
            </div>
          </div>
        </div>
      );
    }

    if (classified.mode === 'include' && classified.sphereTypes.length > 0) {
      const typeLabels = classified.sphereTypes
        .map(
          (name) =>
            schemaMetadata.get(name)?.displayName ?? formatSchemaLabel(name),
        )
        .join(', ');
      return (
        <div className="marketplace-applicability-renderer__constraint">
          <div className="marketplace-applicability-renderer__constraint-header">
            <span
              className="marketplace-applicability-renderer__constraint-icon"
              aria-hidden="true"
            >
              <i className="pi pi-filter" />
            </span>
            <div className="marketplace-applicability-renderer__constraint-text">
              <div className="marketplace-applicability-renderer__constraint-title">
                {t(
                  'marketplace.approachDetail.overview.applicability.onlyTypesTitle',
                  {
                    types: typeLabels,
                    count: classified.sphereTypes.length,
                  },
                )}
              </div>
              <div className="marketplace-applicability-renderer__constraint-description">
                {t(
                  'marketplace.approachDetail.overview.applicability.onlyTypesDescription',
                )}
              </div>
            </div>
          </div>
          <div className="marketplace-applicability-renderer__constraint-divider" />
          <div className="marketplace-applicability-renderer__allowed-types">
            <div className="marketplace-applicability-renderer__allowed-types-label">
              {t(
                'marketplace.approachDetail.overview.applicability.allowedTypesLabel',
              )}
            </div>
            {renderTypeChips(classified.sphereTypes, false)}
          </div>
        </div>
      );
    }

    if (classified.mode === 'exclude' && classified.sphereTypes.length > 0) {
      return (
        <div className="marketplace-applicability-renderer__row">
          <i className="pi pi-ban" aria-hidden="true" />
          <div className="marketplace-applicability-renderer__row-body">
            <div className="marketplace-applicability-renderer__intro">
              {t(
                'marketplace.approachDetail.overview.applicability.allExceptIntro',
              )}
            </div>
            {renderTypeChips(classified.sphereTypes, true)}
          </div>
        </div>
      );
    }

    const expressions = filters ? collectFallbackExpressions(filters) : [];
    return (
      <div className="marketplace-applicability-renderer__row marketplace-applicability-renderer__row--fallback">
        <i className="pi pi-info-circle" aria-hidden="true" />
        <div className="marketplace-applicability-renderer__row-body">
          <div className="marketplace-applicability-renderer__intro">
            {t('marketplace.approachDetail.overview.applicability.fallback')}
          </div>
          {expressions.length > 0 && (
            <ul className="marketplace-applicability-renderer__filter-list">
              {expressions.map((expr, idx) =>
                renderFallbackExpression(expr, idx),
              )}
            </ul>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className={rootClasses}>
      {header ?? (
        <header className="marketplace-applicability-renderer__header">
          {t('marketplace.approachDetail.overview.applicability.sectionTitle')}
        </header>
      )}
      {renderBody()}
    </section>
  );
};

ApplicabilityRendererMolecule.displayName = 'ApplicabilityRendererMolecule';

export default ApplicabilityRendererMolecule;
